import { Button, CircularProgress, IconButton, Tooltip } from "@mui/material";
import "./IngameReportForm.css";
import { BugReport, Check, Close, DragIndicator, Feedback, Mms, Replay } from "@mui/icons-material";
import React, { useEffect, useRef, useState } from "react";

function IngameReportForm(props: {
  projectSlug: string;
  currEventNumber?: number;
  tableId?: string;
  visible: boolean;
}) {
  const topBarOffset = 65;
  const baseDialogWidth = 250;
  const baseDialogHeight = 200;

  let initialDialogWidth = localStorage.getItem("ingameReportFormWidth-" + props.projectSlug)
    ? parseInt(localStorage.getItem("ingameReportFormWidth-" + props.projectSlug) as string)
    : baseDialogWidth;
  let initialDialogHeight = localStorage.getItem("ingameReportFormHeight-" + props.projectSlug)
    ? parseInt(localStorage.getItem("ingameReportFormHeight-" + props.projectSlug) as string)
    : baseDialogHeight;
  const [selectedType, setSelectedType] = useState<string>("game-feedback");
  const [dialogLeft, setDialogLeft] = useState<number>(
    localStorage.getItem("ingameReportFormLeft-" + props.projectSlug)
      ? parseInt(localStorage.getItem("ingameReportFormLeft-" + props.projectSlug) as string)
      : 0,
  );
  const [dialogTop, setDialogTop] = useState<number>(
    localStorage.getItem("ingameReportFormTop-" + props.projectSlug)
      ? parseInt(localStorage.getItem("ingameReportFormTop-" + props.projectSlug) as string)
      : topBarOffset,
  );

  const [feedbackValue, setFeedbackValue] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitResult, setSubmitResult] = useState<string>("");

  const dragHandleXOffset = useRef<number>(0);
  const dragHandleYOffset = useRef<number>(0);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const dialogWidth = useRef<number>(0);
  const dialogHeight = useRef<number>(0);

  useEffect(() => {
    if (window.innerWidth < dialogLeft || window.innerHeight < dialogTop) {
      setDialogLeft(0);
      setDialogTop(topBarOffset);
    }
  }, [dialogLeft, dialogTop, props.visible]);

  function handleMouseMove(event: MouseEvent) {
    event.preventDefault();
    let newLeft = event.clientX - dragHandleXOffset.current;
    let newTop = event.clientY - dragHandleYOffset.current;

    if (newLeft < 0) newLeft = 0;
    if (newLeft + dialogWidth.current > window.innerWidth) newLeft = window.innerWidth - dialogWidth.current;
    if (newTop < topBarOffset) newTop = topBarOffset;
    if (newTop + dialogHeight.current > window.innerHeight) newTop = window.innerHeight - dialogHeight.current;

    setDialogLeft(newLeft);
    setDialogTop(newTop);
  }

  function handleMouseUp(event: MouseEvent) {
    event.preventDefault();
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
    let rect = dialogRef.current?.getBoundingClientRect();
    if (rect) {
      localStorage.setItem("ingameReportFormLeft-" + props.projectSlug, rect.left.toString());
      localStorage.setItem("ingameReportFormTop-" + props.projectSlug, rect.top.toString());
    }
  }

  function handleMouseDown(event: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    if (event.button !== 0) return; // only respond to left mouse button
    event.preventDefault();
    let rect = dialogRef.current?.getBoundingClientRect();
    if (rect) {
      dialogWidth.current = rect.width;
      dialogHeight.current = rect.height;
    }
    dragHandleXOffset.current = event.clientX - dialogLeft;
    dragHandleYOffset.current = event.clientY - dialogTop;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }

  function handleResizeMouseDown(event: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    if (event.button !== 0) return; // only respond to left mouse button
    document.addEventListener("mouseup", handleResizeMouseUp);
  }

  function handleResizeMouseUp(event: MouseEvent) {
    document.removeEventListener("mouseup", handleResizeMouseUp);
    let rect = dialogRef.current?.getBoundingClientRect();
    if (rect) {
      localStorage.setItem("ingameReportFormWidth-" + props.projectSlug, rect.width.toString());
      localStorage.setItem("ingameReportFormHeight-" + props.projectSlug, rect.height.toString());
    }
  }

  function handleDialogReset() {
    setDialogLeft(0);
    setDialogTop(topBarOffset);
    initialDialogWidth = baseDialogWidth;
    initialDialogHeight = baseDialogHeight;
    localStorage.removeItem("ingameReportFormLeft-" + props.projectSlug);
    localStorage.removeItem("ingameReportFormTop-" + props.projectSlug);
    localStorage.removeItem("ingameReportFormWidth-" + props.projectSlug);
    localStorage.removeItem("ingameReportFormHeight-" + props.projectSlug);
  }

  function handleSubmit() {
    let trimmedFeedback = feedbackValue.trim();
    if (trimmedFeedback.length === 0) {
      return;
    }
    setSubmitting(true);
    fetch("/api/ingame-feedback/submit", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        projectSlug: props.projectSlug,
        tableId: props.tableId,
        responseType: selectedType,
        responseValue: trimmedFeedback,
        eventNumber: props.currEventNumber,
      }),
    })
      .then((res) => {
        setSubmitting(false);
        if (res.ok) {
          setFeedbackValue("");
          setSubmitResult("ok");
          setTimeout(() => {
            setSubmitResult("");
          }, 2000);
        } else {
          setSubmitResult("nook");
          setTimeout(() => {
            setSubmitResult("");
          }, 2000);
        }
      })
      .catch((e) => {
        console.error("Error submitting in-game feedback:", e);
        setSubmitting(false);
        setSubmitResult("nook");
        setTimeout(() => {
          setSubmitResult("");
        }, 2000);
      });
  }

  const commonButtonSx = {
    minWidth: "unset",
    padding: "3px 9px",
  };

  let titleText = "";
  switch (selectedType) {
    case "game-feedback":
      titleText = "Game Feedback";
      break;
    case "bug-report":
      titleText = "Bug Report";
      break;
    case "cgo-feedback":
      titleText = "CGO Feedback";
      break;
  }

  return (
    <div
      className="ingame-report-form-container"
      style={{
        top: dialogTop,
        left: dialogLeft,
        width: initialDialogWidth,
        height: initialDialogHeight,
        display: props.visible ? "flex" : "none",
      }}
      ref={dialogRef}
      onMouseDown={handleResizeMouseDown}
    >
      <div className="ingame-report-form-type-selector-drag-bar">
        <div className="ingame-report-form-type-selector">
          <Button
            variant={selectedType === "game-feedback" ? "contained" : "outlined"}
            sx={commonButtonSx}
            onClick={() => setSelectedType("game-feedback")}
          >
            <Feedback />
          </Button>
          <Button
            variant={selectedType === "bug-report" ? "contained" : "outlined"}
            sx={commonButtonSx}
            onClick={() => setSelectedType("bug-report")}
          >
            <BugReport />
          </Button>
          <Button
            variant={selectedType === "cgo-feedback" ? "contained" : "outlined"}
            sx={commonButtonSx}
            onClick={() => setSelectedType("cgo-feedback")}
          >
            <Mms />
          </Button>
        </div>
        <div className="ingame-report-form-reset-button-drag-handle">
          <Tooltip title="Reset size and position" arrow>
            <IconButton color="primary" onClick={handleDialogReset} sx={{ padding: "0" }}>
              <Replay />
            </IconButton>
          </Tooltip>
          <div className="ingame-report-drag-handle" onMouseDown={handleMouseDown}>
            <DragIndicator />
          </div>
        </div>
      </div>
      <span className="ingame-report-form-title">{titleText}</span>
      <textarea
        id="ingame-report-form-textarea"
        className="ingame-report-form-textarea"
        placeholder="..."
        value={feedbackValue}
        onChange={(e) => setFeedbackValue(e.target.value)}
      />
      <div className="ingame-report-form-submit-container">
        <Button variant="contained" onClick={handleSubmit} style={{ flex: 1 }}>
          Submit
        </Button>
        {submitting && <CircularProgress size={24} sx={{ marginLeft: "10px" }} />}
        {!submitting && submitResult === "ok" && <Check color="success" sx={{ marginLeft: "10px" }} />}
        {!submitting && submitResult === "nook" && <Close color="error" sx={{ marginLeft: "10px" }} />}
      </div>
    </div>
  );
}

export default IngameReportForm;
